import _ from 'lodash'
import moment from 'moment'
import config from '../../config'
import {
  ConcreteLevel,
  ConcreteResistanceLevel,
  ConcreteVoltageLevel,
  Device,
  Log,
  State,
} from '../../types'
import { getLoginUserDeviceIds } from '../Auth/selectors'
import { getTimestamp } from '../Clock/selectors'
import { getUser } from '../UserById/selectors'

export const getDevice = (state: State, deviceId: string) =>
  state.DeviceById[deviceId]
export const getDeviceIds = (state: State) => _.keys(state.DeviceById)

// ConcreteMonitor

export const getVisibleConcreteMonitorDeviceIds = (
  state: State,
  visible: boolean = true,
) => {
  const devices = getConcreteMonitorDevices(state)
  const list = _.sortBy(
    visible ? devices.filter(v => v.info.visible) : devices,
    [v => v.info.priority, v => v.info.label],
  )

  return list.map(v => v.id)
}

export const getConcreteMonitorDevices = (state: State) => {
  const deviceIds = getLoginUserDeviceIds(state)

  return _.filter(
    deviceIds.map(did => getConcreteMonitorDevice(state, did)),
    v => !!v,
  ) as Device[]
}

export const getCurrentUser = (state: State, deviceId: string) => {
  const device = getDevice(state, deviceId)

  return getUser(state, device.currentUserId)
}

export const getConcreteMonitorDevice = (state: State, deviceId: string) => {
  const device = getDevice(state, deviceId)

  if (!device) {
    return undefined
  }
  const now = getTimestamp(state)

  return convertConcreteMonitorDevice(device, now)
}

const levelExpand = (
  level: ConcreteLevel,
): {
  resistanceLevel: ConcreteResistanceLevel
  voltageLevel: ConcreteVoltageLevel
} => ({
  resistanceLevel: ['X', 'D', 'C', 'B', 'A', 'A', 'A', 'A'][
    level
  ] as ConcreteResistanceLevel,
  voltageLevel: ['X', 'D', 'A', 'A', 'A', 'B', 'C', 'C'][
    level
  ] as ConcreteVoltageLevel,
})

export const convertConcreteMonitorLog = (log: any): Log => {
  const rawSensors = log.fields.sensors || {}
  const maxId = Math.max(0, ...Object.keys(rawSensors).map(Number))
  const channelCount = maxId > 11 ? 22 : 11

  if (config.isDev) {
    // eslint-disable-next-line no-console
    console.log('[convertConcreteMonitorLog]', {
      rawKeys: Object.keys(rawSensors),
      maxId,
      channelCount,
    })
  }

  const sensors = _.map(_.range(1, channelCount + 1), id => {
    const sensor = rawSensors[id]
    const level: ConcreteLevel = (sensor && sensor.level) || 0

    return {
      ...sensor,
      level,
      ...levelExpand(level),
    }
  })

  return {
    ...log,
    fields: {
      sensors,
    },
  }
}

// 変換済み lastLogs を「生デバイスオブジェクトの参照」でキャッシュする。
// reducer が更新デバイスだけ新参照にするので、未更新デバイスは clock tick を跨いで
// 同一参照→キャッシュヒットし、重いセンサー変換 (levelExpand 等) を再実行しない。
// 結果として lastLog/sensors の参照も安定し、下流の React.memo が効く。
const convertedLastLogsCache = new WeakMap<object, { [session: number]: Log }>()

const getConvertedLastLogs = (device: any): { [session: number]: Log } => {
  const cached = convertedLastLogsCache.get(device)

  if (cached) {
    return cached
  }
  const converted = _.mapValues(device.lastLogs, convertConcreteMonitorLog)

  convertedLastLogsCache.set(device, converted)

  return converted
}

const convertConcreteMonitorDevice = (device: any, now: number): Device => {
  const info = device.info || {}

  return {
    ...device,
    lastLogs: getConvertedLastLogs(device),
    info: {
      priority: info.priority || 0,
      label: info.label || '',
      visible: info.visible || false,
      threasholds: info.threasholds,
      memo: info.memo || '',
      token: info.token || '',
      ...convertKeep(device.updatedAt, device.keepedAt, now),
      status: info.status || undefined,
    },
  }
}

function convertKeep(
  timestamp: undefined | number,
  keepTime: undefined | number,
  now: number,
): {
  keepedStr: string
  keeped: boolean
  // keepedNewSession: boolean
  keepExpired: boolean
} {
  if (!timestamp) {
    return {
      keepedStr: 'データなし',
      // keepedNewSession: false,
      keeped: false,
      keepExpired: false,
    }
  }
  // console.log({ keepTime })
  if (!keepTime) {
    keepTime = timestamp
  }
  const ts = moment(timestamp)
  const kts = moment(keepTime)
  const keeped = kts.isAfter(moment(now).subtract(1, 'minute'))
  const keepExpired = ts.isBefore(
    moment(now).subtract(config.keepExpiredMinutes, 'minute'),
  )

  // console.log({ keeped, keepTime, keepedNewSession, keepExpired })

  return {
    keepedStr: `${keeped ? '接続あり' : '接続なし'}`,
    // keepedStr: `${keeped ? '記録中' : '記録停止中'} (${ts.fromNow()})`,
    keeped,
    // keepedNewSession: keepExpired,
    keepExpired,
  }
}
